// src/services/auditStore.ts
import { ActionPlan, AuditQuestion, AuditRecord, AuditTemplate, Frequency } from '../auditTypes';

const REC_KEY = 'rmacs_audit_records_v1';
const ACT_KEY = 'rmacs_action_plans_v1';

export const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36);
const load = <T,>(k: string, f: T): T => { try { const r = localStorage.getItem(k); return r ? (JSON.parse(r) as T) : f; } catch { return f; } };
const save = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

export const getAuditRecords = (): AuditRecord[] => load(REC_KEY, []);
export const saveAuditRecord = (r: AuditRecord) => save(REC_KEY, [...getAuditRecords().filter(x => x.id !== r.id), r]);
export const getActionPlans = (): ActionPlan[] => load(ACT_KEY, []);
export const upsertActionPlan = (p: ActionPlan) => save(ACT_KEY, [...getActionPlans().filter(x => x.id !== p.id), p]);

export const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
export const todayISO = () => new Date().toISOString();
export const addDays = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };

export function computeScore(answers: Record<string, { value: string | null }>, questions: AuditQuestion[]): number | null {
  let pts = 0, max = 0;
  for (const q of questions) {
    const a = answers[q.id]; if (!a || !a.value) return null;
    if (a.value === 'na') continue;
    max += 1; pts += a.value === 'yes' ? 1 : a.value === 'partial' ? 0.5 : 0;
  }
  return max === 0 ? 100 : Math.round((pts / max) * 100);
}

const WINDOW: Record<Frequency, number> = { daily: 1, weekly: 7, monthly: 31, quarterly: 92, annual: 366 };

export type DueStatus = 'ok' | 'due' | 'overdue';
export function dueInfo(t: AuditTemplate, records: AuditRecord[]): { status: DueStatus; label: string; last?: AuditRecord } {
  const done = records.filter(r => r.templateId === t.id && r.status === 'completed')
    .sort((a, b) => b.dateISO.localeCompare(a.dateISO))[0];
  if (!done) return { status: 'due', label: 'Never completed' };
  const days = Math.floor((Date.now() - new Date(done.dateISO).getTime()) / 86400000);
  const w = WINDOW[t.frequency];
  if (days > w) return { status: 'overdue', label: `Overdue – last ${fmtDate(done.dateISO)}`, last: done };
  if (days === w) return { status: 'due', label: `Due – last ${fmtDate(done.dateISO)}`, last: done };
  return { status: 'ok', label: `Done ${fmtDate(done.dateISO)} (${done.score ?? '–'}%)`, last: done };
}

export const isOverdue = (p: ActionPlan) => p.status !== 'closed' && p.dueDate < new Date().toISOString().slice(0, 10);